// code-guardian-ignore-duplicates: settings-page scaffolding; the dismissable save banner mirrors RedFlagRulesPage's and there is no shared banner component to import (extracting one is a UI change, out of scope here)
// Connect content policy — Governance → Connect Content Policy.
//
// Per project: the rules the AI (Gemini) reviews every Connect post, edit,
// quote-repost and comment against, what each rule does, and the policy mode:
//   off      — nothing is checked;
//   monitor  — checked AFTER publishing, nothing blocked; would-be blocks/holds land
//              in Social moderation › AI review (the default for every project);
//   enforce  — checked BEFORE publishing: Block refuses the post (the author is told
//              which rule and how to fix it), Hold keeps it visible only to its author
//              until a moderator approves it. An AI outage never stops posting — the
//              item is published and queued as "Not checked".
//
// Every save is a new VERSION (history below the header, any version can be loaded
// back into the editor); a review row records the version it was judged against.
//
// ── ACCESS ──────────────────────────────────────────────────────────────────
// Reading and testing need moderator standing for the project (the same check as
// Social Moderation: `web.social_moderate` + project-wide data scope, super admins
// always pass); a 403 on load is the full-page AccessDeniedPanel. CHANGING the policy
// additionally needs `web.social_policy` — the GET says so in `canEdit`, and without
// it the editor renders read-only rather than refusing on Save. The server enforces
// both; nothing here is a security boundary.
//
// Named "Connect content policy", never "compliance policy": that already means the
// store grooming/uniform rules.

import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  AlertTriangle,
  CheckCircle2,
  History,
  Image as ImageIcon,
  Info,
  Loader2,
  Plus,
  RotateCcw,
  Save,
  ShieldCheck,
  Sparkles,
  Trash2,
  Undo2,
  X,
} from 'lucide-react';
import { TableActionButton, TableActionGroup } from '@channelplay/table-actions';
import { useAuthStore } from '../stores/authStore';
import { socialFeedAPI } from '../services/api';
import {
  connectPolicyAPI,
  connectPolicyErrorMessage,
  connectPolicyErrorStatus,
  parseServerTimestamp,
  type AiVerdict,
  type ConnectPolicyState,
  type ContentPolicy,
  type PolicyMode,
  type PolicyRule,
  type PolicyVersionSummary,
  type RuleAction,
  type StoredPolicy,
  type Violation,
} from '../services/connectPolicyApi';
import { NoProjectSelectedState } from '../components/NoProjectSelectedState';
import { AccessDeniedPanel } from '../components/AccessDeniedPanel';
import { PageErrorBanner } from '../components/PageErrorBanner';
import { ModalFormErrorBanner } from '../components/ModalFormErrorBanner';
import { Skeleton } from '../components/Skeleton';
import { InlineLoader } from '../components/loading';
import Modal from '../components/Modal';

// ── Limits (mirrored from the server's sanitizePolicy so the editor explains
//    before a round trip; the server re-checks every one of them) ─────────────
const MAX_RULES = 30;
const NAME_MAX = 80;
const DESCRIPTION_MAX = 800;
const TEXT_MAX = 4000;
const NOTE_MAX = 500;
const TEST_TEXT_MAX = 4000;
const TEST_MAX_PHOTOS = 4;
// The reviewer skips photos above this size, so a test with one would not
// reflect what happens to a real post.
const TEST_PHOTO_MAX_BYTES = 7 * 1024 * 1024;

const NO_ACCESS_MESSAGE =
  'You don’t have access to the Connect content policy for this project. Viewing it needs the Social Moderation page ticked for your role and project-wide data visibility; changing it also needs the Connect Content Policy page ticked. An administrator can tick both in Access Management → Web Access.';

const MODE_OPTIONS: Array<{ value: PolicyMode; label: string; description: string }> = [
  {
    value: 'off',
    label: 'Off',
    description: 'Nothing is checked. Posts, edits and comments are shared as they are.',
  },
  {
    value: 'monitor',
    label: 'Monitor only',
    description:
      'AI checks every post after it’s published and blocks nothing. Anything it would block or hold appears in Social moderation › AI review.',
  },
  {
    value: 'enforce',
    label: 'Enforce',
    description:
      'AI checks every post, edit and comment before it’s shared. Block rules stop it; Hold rules keep it visible only to its author until a moderator approves it. If the AI can’t be reached, it’s shared and marked Not checked.',
  },
];

const MODE_LABEL: Record<PolicyMode, string> = { off: 'Off', monitor: 'Monitor only', enforce: 'Enforce' };

const ACTION_OPTIONS: Array<{ value: RuleAction; label: string }> = [
  { value: 'block', label: 'Block' },
  { value: 'hold', label: 'Hold for review' },
  { value: 'off', label: 'Off' },
];

const ACTION_LABEL: Record<RuleAction, string> = { block: 'Block', hold: 'Hold for review', off: 'Off' };

const ACTION_TONE: Record<RuleAction, string> = {
  block: 'border-red-300 bg-red-50 text-red-800',
  hold: 'border-amber-300 bg-amber-50 text-amber-900',
  off: 'border-gray-300 bg-gray-50 text-gray-600',
};

const MODE_AFTER_SAVE: Record<PolicyMode, string> = {
  off: 'The AI is not checking Connect posts in this project.',
  monitor:
    'New posts, edits and comments are checked after they’re published. Nothing is blocked; anything the AI would block or hold appears in Social moderation › AI review.',
  enforce:
    'New posts, edits and comments are checked before they’re shared. Nothing already posted is re-checked.',
};

// ── Pure helpers ─────────────────────────────────────────────────────────────

const clonePolicy = (p: ContentPolicy): ContentPolicy => ({
  mode: p.mode,
  rules: p.rules.map((r) => ({ ...r })),
  allowed: p.allowed,
  guidelines: p.guidelines,
});

/**
 * Coerces a policy from the server into the editor's shape and puts back any
 * BUILT-IN rule it lacks, switched Off. The server refuses a save that drops a
 * built-in rule, so an older version (saved before a rule joined the default)
 * would otherwise load fine and then fail on Save with no visible cause. Off keeps
 * the loaded version's meaning — a rule it never had still does nothing.
 */
const normalizePolicy = (
  raw: Partial<ContentPolicy> | null | undefined,
  defaults: ContentPolicy,
): { policy: ContentPolicy; added: string[] } => {
  const mode: PolicyMode = raw?.mode === 'off' || raw?.mode === 'enforce' || raw?.mode === 'monitor' ? raw.mode : 'monitor';
  const rules: PolicyRule[] = Array.isArray(raw?.rules)
    ? raw!.rules.map((r) => ({
        id: String(r.id ?? ''),
        name: String(r.name ?? ''),
        description: String(r.description ?? ''),
        action: r.action === 'block' || r.action === 'hold' ? r.action : 'off',
        builtIn: Boolean(r.builtIn),
      }))
    : [];
  const have = new Set(rules.map((r) => r.id));
  const added: string[] = [];
  for (const d of defaults.rules) {
    if (d.builtIn && !have.has(d.id)) {
      rules.push({ ...d, action: 'off' });
      added.push(d.name);
    }
  }
  return {
    policy: {
      mode,
      rules,
      allowed: typeof raw?.allowed === 'string' ? raw.allowed : '',
      guidelines: typeof raw?.guidelines === 'string' ? raw.guidelines : '',
    },
    added,
  };
};

const fingerprint = (p: ContentPolicy | null): string => (p ? JSON.stringify(p) : '');

/** Rows for a rule description box: enough for the text at desktop width (about 90
 *  characters a line) plus one spare, between 2 and 8, so a default rule is never cut
 *  off mid-line. The box can still be resized by hand. */
const descriptionRows = (text: string): number => {
  const lines = text.split('\n').reduce((n, line) => n + Math.max(1, Math.ceil(line.length / 90)), 0);
  return Math.min(8, Math.max(2, lines + 1));
};

/** A stable id for a new custom rule. Deliberately NOT derived from the name: the
 *  id is what review rows reference, and the name stays editable. */
const newRuleId = (taken: Set<string>): string => {
  let id = `custom_${Date.now().toString(36)}`;
  let n = 1;
  while (taken.has(id)) id = `custom_${Date.now().toString(36)}_${n++}`;
  return id;
};

interface PolicyErrors {
  byRule: Map<string, string[]>;
  general: string[];
}

const validatePolicy = (p: ContentPolicy): PolicyErrors => {
  const byRule = new Map<string, string[]>();
  const general: string[] = [];
  if (p.rules.length > MAX_RULES) general.push(`A policy can have at most ${MAX_RULES} rules.`);
  for (const r of p.rules) {
    const errs: string[] = [];
    if (!r.name.trim()) errs.push('Give this rule a name.');
    if (r.name.length > NAME_MAX) errs.push(`Keep the name to ${NAME_MAX} characters.`);
    if (!r.description.trim()) errs.push('Describe what this rule catches — the AI judges posts by this description.');
    if (r.description.length > DESCRIPTION_MAX) errs.push(`Keep the description to ${DESCRIPTION_MAX} characters.`);
    if (errs.length) byRule.set(r.id, errs);
  }
  if (p.allowed.length > TEXT_MAX) general.push(`Keep “Always allowed” to ${TEXT_MAX} characters.`);
  if (p.guidelines.length > TEXT_MAX) general.push(`Keep “Extra guidelines” to ${TEXT_MAX} characters.`);
  return { byRule, general };
};

/** One line per difference, for the Save dialog. */
const summariseChanges = (base: ContentPolicy, next: ContentPolicy): string[] => {
  const lines: string[] = [];
  if (base.mode !== next.mode) lines.push(`Mode: ${MODE_LABEL[base.mode]} → ${MODE_LABEL[next.mode]}`);
  const baseById = new Map(base.rules.map((r) => [r.id, r]));
  const nextIds = new Set(next.rules.map((r) => r.id));
  for (const r of next.rules) {
    const b = baseById.get(r.id);
    const name = r.name.trim() || 'Untitled rule';
    if (!b) {
      lines.push(`Added “${name}” (${ACTION_LABEL[r.action]})`);
      continue;
    }
    if (b.name !== r.name) lines.push(`Renamed “${b.name}” to “${name}”`);
    if (b.action !== r.action) lines.push(`“${name}”: ${ACTION_LABEL[b.action]} → ${ACTION_LABEL[r.action]}`);
    if (b.description !== r.description) lines.push(`Edited the description of “${name}”`);
  }
  for (const b of base.rules) if (!nextIds.has(b.id)) lines.push(`Removed “${b.name}”`);
  if (base.allowed !== next.allowed) lines.push('Edited “Always allowed”');
  if (base.guidelines !== next.guidelines) lines.push('Edited “Extra guidelines”');
  return lines;
};

const fmtWhen = (value: string | null | undefined): string => {
  const d = parseServerTimestamp(value ?? null);
  if (!d) return value || '—';
  return d.toLocaleString('en-IN', { day: 'numeric', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' });
};

const savedByText = (p: { createdBy: string | null; createdByName?: string | null }): string =>
  p.createdByName?.trim() || p.createdBy || 'unknown';

// ── Small presentational pieces ──────────────────────────────────────────────

function ActionChip({ action }: { action: RuleAction }) {
  return (
    <span className={`inline-flex items-center rounded-full border px-2 py-0.5 text-xs font-medium ${ACTION_TONE[action]}`}>
      {ACTION_LABEL[action]}
    </span>
  );
}

interface Banner {
  tone: 'success' | 'info';
  title: string;
  lines: string[];
}

// ── Page ─────────────────────────────────────────────────────────────────────

type LoadState = 'loading' | 'ready' | 'forbidden' | 'error';

export default function ConnectContentPolicyPage() {
  const projectId = useAuthStore((s) => s.selectedProjectId);
  const clearAuth = useAuthStore((s) => s.clearAuth);
  const navigate = useNavigate();

  const [loadState, setLoadState] = useState<LoadState>('loading');
  const [loadError, setLoadError] = useState<string | null>(null);
  const [server, setServer] = useState<ConnectPolicyState | null>(null);
  const [baseline, setBaseline] = useState<ContentPolicy | null>(null);
  const [draft, setDraft] = useState<ContentPolicy | null>(null);

  const [banner, setBanner] = useState<Banner | null>(null);

  const [saveOpen, setSaveOpen] = useState(false);
  const [saveNote, setSaveNote] = useState('');
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);

  const [showHistory, setShowHistory] = useState(false);
  const [versions, setVersions] = useState<PolicyVersionSummary[] | null>(null);
  const [versionsLoading, setVersionsLoading] = useState(false);
  const [versionsError, setVersionsError] = useState<string | null>(null);
  const [fetchingVersion, setFetchingVersion] = useState<number | null>(null);
  const [viewing, setViewing] = useState<StoredPolicy | null>(null);

  // Drops responses that belong to a previously selected project.
  const seq = useRef(0);

  const canEdit = !!server?.canEdit;
  const aiAvailable = server?.aiAvailable ?? true;
  const stored = server?.policy ?? null;
  const defaults = server?.defaultPolicy ?? null;

  const draftKey = useMemo(() => fingerprint(draft), [draft]);
  const dirty = canEdit && !!draft && !!baseline && draftKey !== fingerprint(baseline);
  const errors = useMemo<PolicyErrors>(
    () => (draft ? validatePolicy(draft) : { byRule: new Map(), general: [] }),
    [draft],
  );
  const hasErrors = errors.general.length > 0 || errors.byRule.size > 0;
  const isDefaultInEditor = useMemo(() => {
    if (!draft || !defaults) return false;
    return draftKey === fingerprint(normalizePolicy(defaults, defaults).policy);
  }, [draft, draftKey, defaults]);

  // ── Load ───────────────────────────────────────────────────────────────────
  const load = useCallback(async () => {
    if (!projectId) return;
    const my = ++seq.current;
    setLoadState('loading');
    setLoadError(null);
    setBanner(null);
    setServer(null);
    setBaseline(null);
    setDraft(null);
    setVersions(null);
    setVersionsError(null);
    setViewing(null);
    setSaveOpen(false);
    try {
      const state = await connectPolicyAPI.getPolicy(projectId);
      if (my !== seq.current) return;
      const { policy } = normalizePolicy(state.policy?.policy, state.defaultPolicy);
      setServer(state);
      setBaseline(policy);
      setDraft(clonePolicy(policy));
      setLoadState('ready');
    } catch (err) {
      if (my !== seq.current) return;
      if (connectPolicyErrorStatus(err) === 403) {
        setLoadState('forbidden');
      } else {
        setLoadError(connectPolicyErrorMessage(err, 'Could not load the Connect content policy.'));
        setLoadState('error');
      }
    }
  }, [projectId]);

  useEffect(() => {
    void load();
  }, [load]);

  // Leaving with unsaved edits asks first (tab close / reload). In-app navigation
  // is not intercepted — same as the other rule editors.
  useEffect(() => {
    if (!dirty) return undefined;
    const warn = (e: BeforeUnloadEvent) => {
      e.preventDefault();
      e.returnValue = '';
    };
    window.addEventListener('beforeunload', warn);
    return () => window.removeEventListener('beforeunload', warn);
  }, [dirty]);

  const loadVersions = useCallback(async () => {
    if (!projectId) return;
    const my = seq.current;
    setVersionsLoading(true);
    setVersionsError(null);
    try {
      const list = await connectPolicyAPI.listVersions(projectId);
      if (my !== seq.current) return;
      setVersions([...list].sort((a, b) => b.version - a.version));
    } catch (err) {
      if (my !== seq.current) return;
      setVersionsError(connectPolicyErrorMessage(err, 'Could not load the version history.'));
    } finally {
      if (my === seq.current) setVersionsLoading(false);
    }
  }, [projectId]);

  useEffect(() => {
    if (showHistory && versions === null && !versionsLoading && !versionsError) void loadVersions();
  }, [showHistory, versions, versionsLoading, versionsError, loadVersions]);

  // ── Editing ────────────────────────────────────────────────────────────────
  const edit = (fn: (p: ContentPolicy) => ContentPolicy) => {
    setDraft((prev) => (prev ? fn(prev) : prev));
    setBanner((b) => (b?.tone === 'success' ? null : b));
  };

  const updateRule = (id: string, patch: Partial<PolicyRule>) =>
    edit((p) => ({ ...p, rules: p.rules.map((r) => (r.id === id ? { ...r, ...patch } : r)) }));

  const removeRule = (id: string) =>
    edit((p) => ({ ...p, rules: p.rules.filter((r) => r.id !== id || r.builtIn) }));

  const addRule = () =>
    edit((p) => {
      if (p.rules.length >= MAX_RULES) return p;
      const id = newRuleId(new Set(p.rules.map((r) => r.id)));
      return { ...p, rules: [...p.rules, { id, name: '', description: '', action: 'hold', builtIn: false }] };
    });

  const discard = () => {
    if (baseline) setDraft(clonePolicy(baseline));
    setBanner(null);
  };

  const resetToDefault = () => {
    if (!defaults) return;
    const ok = window.confirm(
      'Load the built-in default policy into the editor?\n\nThis replaces the mode (Monitor only), every rule, “Always allowed” and “Extra guidelines” in the editor, and removes your custom rules. Nothing changes for employees until you save.',
    );
    if (!ok) return;
    setDraft(normalizePolicy(defaults, defaults).policy);
    setBanner({
      tone: 'info',
      title: 'Loaded the built-in default into the editor',
      lines: ['Review it, then save to make it the active policy.'],
    });
  };

  const loadIntoEditor = (v: StoredPolicy) => {
    if (!defaults) return;
    const { policy, added } = normalizePolicy(v.policy, defaults);
    setDraft(policy);
    setViewing(null);
    const label = v.isDefault || v.version === 0 ? 'the built-in default' : `v${v.version}`;
    setBanner({
      tone: 'info',
      title: `Loaded ${label} into the editor`,
      lines: [
        'Save to make it the active policy — until then nothing changes for employees.',
        ...(added.length
          ? [`Built-in rules added since then are included as Off: ${added.join(', ')}.`]
          : []),
      ],
    });
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const fetchVersion = async (version: number, then: 'view' | 'load') => {
    if (!projectId) return;
    if (then === 'load' && dirty && !window.confirm('Replace your unsaved changes in the editor with this version?')) return;
    const my = seq.current;
    setFetchingVersion(version);
    setVersionsError(null);
    try {
      const v = await connectPolicyAPI.getVersion(projectId, version);
      if (my !== seq.current) return;
      if (then === 'view') setViewing(v);
      else loadIntoEditor(v);
    } catch (err) {
      if (my !== seq.current) return;
      setVersionsError(connectPolicyErrorMessage(err, `Could not load v${version}.`));
    } finally {
      if (my === seq.current) setFetchingVersion(null);
    }
  };

  // ── Save ───────────────────────────────────────────────────────────────────
  const openSave = () => {
    setSaveError(null);
    setSaveOpen(true);
  };

  const confirmSave = async () => {
    if (!projectId || !draft || !defaults) return;
    const my = seq.current;
    setSaving(true);
    setSaveError(null);
    try {
      const saved = await connectPolicyAPI.savePolicy(projectId, draft, saveNote);
      if (my !== seq.current) return;
      const { policy } = normalizePolicy(saved.policy, defaults);
      setServer((s) => (s ? { ...s, policy: saved } : s));
      setBaseline(policy);
      setDraft(clonePolicy(policy));
      setSaveOpen(false);
      setSaveNote('');
      setBanner({
        tone: 'success',
        title: `Saved as v${saved.version}`,
        lines: [
          MODE_AFTER_SAVE[policy.mode],
          // The server caches the active policy per project for up to a minute.
          'It can take up to a minute to apply everywhere.',
          ...(!aiAvailable ? ['AI review is switched off on this server, so this takes effect once it’s switched back on.'] : []),
        ],
      });
      // The list is stale now; refetch if it's showing, else on next open.
      setVersions(null);
    } catch (err) {
      if (my !== seq.current) return;
      setSaveError(
        connectPolicyErrorStatus(err) === 403
          ? connectPolicyErrorMessage(err, 'Only super admins, or people whose role has the Connect Content Policy page ticked, can change the policy.')
          : connectPolicyErrorMessage(err, 'Could not save the policy. Your changes are still in the editor.'),
      );
    } finally {
      if (my === seq.current) setSaving(false);
    }
  };

  // ── Render ─────────────────────────────────────────────────────────────────
  const versionBadge = stored && (
    stored.isDefault || stored.version === 0 ? (
      <span className="inline-flex items-center rounded-full bg-gray-100 px-2.5 py-0.5 text-xs font-medium text-gray-700">
        Built-in default
      </span>
    ) : (
      <span className="inline-flex flex-wrap items-center rounded-full bg-primary-50 px-2.5 py-0.5 text-xs font-medium text-primary-800">
        v{stored.version} · saved by {savedByText(stored)} on {fmtWhen(stored.createdAt)}
      </span>
    )
  );

  const header = (
    <div>
      <div className="flex flex-wrap items-center gap-2">
        <h1 className="text-2xl font-bold text-gray-900">Connect content policy</h1>
        {versionBadge}
      </div>
      <p className="mt-1 text-sm text-gray-500">The rules the AI uses to review Connect posts, edits and comments</p>
    </div>
  );

  if (!projectId) {
    return (
      <div className="space-y-6">
        {header}
        <NoProjectSelectedState message="Select a project from the dropdown in the menu bar to see its Connect content policy." />
      </div>
    );
  }

  if (loadState === 'forbidden') {
    return (
      <AccessDeniedPanel
        message={NO_ACCESS_MESSAGE}
        onLogout={() => { clearAuth(); navigate('/login'); }}
      />
    );
  }

  if (loadState === 'error') {
    return (
      <div className="space-y-6">
        {header}
        <PageErrorBanner message={loadError ?? 'Could not load the Connect content policy.'} />
        <button
          type="button"
          onClick={() => void load()}
          className="rounded-lg border border-gray-300 px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50"
        >
          Try again
        </button>
      </div>
    );
  }

  if (loadState === 'loading' || !draft || !stored || !defaults) {
    return (
      <div className="space-y-6">
        {header}
        <div className="space-y-3" aria-busy="true" aria-label="Loading policy">
          <Skeleton className="h-24 w-full" />
          {Array.from({ length: 4 }).map((_, i) => <Skeleton key={i} className="h-28 w-full" />)}
        </div>
      </div>
    );
  }

  const counts = draft.rules.reduce(
    (acc, r) => ({ ...acc, [r.action]: acc[r.action] + 1 }),
    { block: 0, hold: 0, off: 0 } as Record<RuleAction, number>,
  );
  const activeLabel = stored.isDefault || stored.version === 0 ? 'the built-in default' : `v${stored.version}`;

  return (
    <div className="space-y-6 pb-4">
      <div className="flex flex-col gap-4 md:flex-row md:items-start md:justify-between">
        {header}
        <button
          type="button"
          aria-pressed={showHistory}
          onClick={() => setShowHistory((v) => !v)}
          className="inline-flex shrink-0 items-center justify-center gap-1.5 self-start rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50"
        >
          <History className="h-4 w-4" aria-hidden />
          Version history
        </button>
      </div>

      {!aiAvailable && (
        <div className="flex items-start gap-2 rounded-lg border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-900" role="status">
          <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" aria-hidden />
          <span>
            AI review is switched off on this server, so nothing is being checked right now — whatever the mode below
            says. You can still edit and save the policy; it takes effect once AI review is switched back on.
          </span>
        </div>
      )}

      {!canEdit && (
        <div className="flex items-start gap-2 rounded-lg border border-blue-200 bg-blue-50 px-4 py-3 text-sm text-blue-900" role="status">
          <Info className="mt-0.5 h-4 w-4 shrink-0" aria-hidden />
          <span>
            You can view this policy and test posts against it, but only super admins and people whose role has the
            Connect Content Policy page ticked can change it.
          </span>
        </div>
      )}

      {banner && (
        <div
          className={`flex items-start justify-between gap-3 rounded-lg border px-4 py-3 text-sm ${
            banner.tone === 'success' ? 'border-emerald-200 bg-emerald-50 text-emerald-900' : 'border-blue-200 bg-blue-50 text-blue-900'
          }`}
          role="status"
        >
          <div className="flex items-start gap-2">
            {banner.tone === 'success'
              ? <CheckCircle2 className="mt-0.5 h-4 w-4 shrink-0" aria-hidden />
              : <Info className="mt-0.5 h-4 w-4 shrink-0" aria-hidden />}
            <div>
              <p className="font-medium">{banner.title}</p>
              {banner.lines.map((l) => <p key={l} className="mt-0.5">{l}</p>)}
            </div>
          </div>
          <button
            type="button"
            onClick={() => setBanner(null)}
            aria-label="Dismiss"
            className="shrink-0 rounded p-0.5 opacity-70 hover:opacity-100"
          >
            <X className="h-4 w-4" aria-hidden />
          </button>
        </div>
      )}

      {showHistory && (
        <VersionHistoryTable
          versions={versions}
          loading={versionsLoading}
          error={versionsError}
          activeVersion={stored.isDefault ? 0 : stored.version}
          canEdit={canEdit}
          fetchingVersion={fetchingVersion}
          // Clearing both lets the history effect refetch — one request, not two.
          onRetry={() => { setVersionsError(null); setVersions(null); }}
          onView={(v) => void fetchVersion(v, 'view')}
          onLoad={(v) => void fetchVersion(v, 'load')}
        />
      )}

      <div className="grid gap-6 xl:grid-cols-[minmax(0,1fr)_380px]">
        <div className="min-w-0 space-y-6">
          {/* ── Mode ─────────────────────────────────────────────────────── */}
          <section aria-labelledby="ccp-mode-title" className="rounded-lg border border-gray-100 bg-white p-4 sm:p-6">
            <h2 id="ccp-mode-title" className="text-lg font-semibold text-gray-900">Mode</h2>
            <p className="text-sm text-gray-500">How the rules below are applied when someone posts.</p>
            <div role="radiogroup" aria-labelledby="ccp-mode-title" className="mt-4 grid gap-3 sm:grid-cols-3">
              {MODE_OPTIONS.map((m) => {
                const selected = draft.mode === m.value;
                return (
                  <button
                    key={m.value}
                    type="button"
                    role="radio"
                    aria-checked={selected}
                    disabled={!canEdit}
                    onClick={() => edit((p) => ({ ...p, mode: m.value }))}
                    // flex-col + justify-start: a <button> centres its content vertically by
                    // default, which put the three titles at three different heights.
                    className={`flex flex-col items-start justify-start rounded-lg border p-3 text-left transition-colors disabled:cursor-default ${
                      selected
                        ? 'border-primary-500 bg-primary-50 ring-1 ring-primary-500'
                        : 'border-gray-200 bg-white hover:border-gray-300 disabled:hover:border-gray-200'
                    }`}
                  >
                    <span className={`block text-sm font-semibold ${selected ? 'text-primary-800' : 'text-gray-900'}`}>
                      {m.label}
                      {m.value === 'monitor' && <span className="ml-1.5 text-xs font-normal text-gray-500">(default)</span>}
                    </span>
                    <span className="mt-1 block text-xs leading-relaxed text-gray-600">{m.description}</span>
                  </button>
                );
              })}
            </div>
            {canEdit && draft.mode === 'enforce' && baseline?.mode !== 'enforce' && (
              <p className="mt-3 flex items-start gap-1.5 text-sm text-amber-800">
                <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" aria-hidden />
                <span>
                  Once saved, employees can’t share anything that breaks a Block rule. Try a few posts in “Test a post”
                  first, and consider running Monitor only for a while to see what would be caught.
                </span>
              </p>
            )}
          </section>

          {/* ── Rules ────────────────────────────────────────────────────── */}
          <section aria-labelledby="ccp-rules-title" className="space-y-3">
            <div className="flex flex-col gap-2 sm:flex-row sm:items-end sm:justify-between">
              <div>
                <h2 id="ccp-rules-title" className="text-lg font-semibold text-gray-900">Rules</h2>
                <p className="text-sm text-gray-500">
                  {draft.rules.length} rule{draft.rules.length === 1 ? '' : 's'} · {counts.block} block · {counts.hold} hold for
                  review · {counts.off} off
                </p>
              </div>
              {canEdit && (
                <button
                  type="button"
                  onClick={addRule}
                  disabled={draft.rules.length >= MAX_RULES}
                  title={draft.rules.length >= MAX_RULES ? `A policy can have at most ${MAX_RULES} rules.` : undefined}
                  className="inline-flex shrink-0 items-center justify-center gap-1.5 rounded-lg border border-primary-300 bg-white px-3 py-2 text-sm font-medium text-primary-700 hover:bg-primary-50 disabled:cursor-not-allowed disabled:opacity-50"
                >
                  <Plus className="h-4 w-4" aria-hidden />
                  Add rule
                </button>
              )}
            </div>
            <ul className="space-y-3">
              {draft.rules.map((rule) => (
                <RuleEditor
                  key={rule.id}
                  rule={rule}
                  editable={canEdit}
                  errors={errors.byRule.get(rule.id) ?? []}
                  onChange={(patch) => updateRule(rule.id, patch)}
                  onRemove={() => removeRule(rule.id)}
                />
              ))}
            </ul>
          </section>

          {/* ── Always allowed + guidelines ─────────────────────────────── */}
          <section aria-labelledby="ccp-allowed-title" className="rounded-lg border border-gray-100 bg-white p-4 sm:p-6">
            <label htmlFor="ccp-allowed" id="ccp-allowed-title" className="block text-lg font-semibold text-gray-900">
              Always allowed
            </label>
            <p className="text-sm text-gray-500">
              Things that are fine to post even when they look like a rule is being broken — these win over the rules.
            </p>
            <textarea
              id="ccp-allowed"
              value={draft.allowed}
              onChange={(e) => edit((p) => ({ ...p, allowed: e.target.value }))}
              readOnly={!canEdit}
              rows={8}
              className="mt-3 block w-full resize-y rounded-lg border border-gray-300 px-3 py-2 text-sm leading-relaxed text-gray-800 focus:border-primary-500 focus:outline-none focus:ring-1 focus:ring-primary-500 read-only:bg-gray-50"
            />
            <p className={`mt-1 text-right text-xs ${draft.allowed.length > TEXT_MAX ? 'text-red-600' : 'text-gray-400'}`}>
              {draft.allowed.length}/{TEXT_MAX}
            </p>

            <label htmlFor="ccp-guidelines" className="mt-4 block text-lg font-semibold text-gray-900">
              Extra guidelines
            </label>
            <p className="text-sm text-gray-500">General guidance for the AI on how to apply the rules.</p>
            <textarea
              id="ccp-guidelines"
              value={draft.guidelines}
              onChange={(e) => edit((p) => ({ ...p, guidelines: e.target.value }))}
              readOnly={!canEdit}
              rows={5}
              className="mt-3 block w-full resize-y rounded-lg border border-gray-300 px-3 py-2 text-sm leading-relaxed text-gray-800 focus:border-primary-500 focus:outline-none focus:ring-1 focus:ring-primary-500 read-only:bg-gray-50"
            />
            <p className={`mt-1 text-right text-xs ${draft.guidelines.length > TEXT_MAX ? 'text-red-600' : 'text-gray-400'}`}>
              {draft.guidelines.length}/{TEXT_MAX}
            </p>
          </section>
        </div>

        {/* ── Test panel — beside the editor on wide screens, below it on phones. */}
        <div className="min-w-0 xl:sticky xl:top-4 xl:self-start">
          <TestPostPanel
            key={projectId}
            projectId={projectId}
            policy={draft}
            policyKey={draftKey}
            testsDraft={dirty}
            policyInvalid={hasErrors}
            activeLabel={activeLabel}
            aiAvailable={aiAvailable}
          />
        </div>
      </div>

      {canEdit && (
        // Sticky so Save stays in reach on a long rule list and on phones.
        <div className="sticky bottom-0 z-20 flex flex-col gap-2 rounded-lg border border-gray-200 bg-white/95 px-4 py-3 shadow-[0_-4px_12px_-6px_rgba(0,0,0,0.15)] backdrop-blur sm:flex-row sm:items-center sm:justify-between">
          <div className="text-sm text-gray-600" aria-live="polite">
            {dirty
              ? <span className="font-medium text-amber-700">Unsaved changes — employees are still on {activeLabel}</span>
              : `Active: ${activeLabel} · ${MODE_LABEL[stored.policy.mode]}`}
            {dirty && hasErrors && (
              <span className="mt-0.5 block text-red-700">
                {errors.general[0] ?? 'Fix the highlighted rules before saving.'}
              </span>
            )}
          </div>
          <div className="flex flex-wrap gap-2">
            <button
              type="button"
              onClick={resetToDefault}
              disabled={isDefaultInEditor || saving}
              className="inline-flex items-center gap-1.5 rounded-lg border border-gray-300 px-3 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50 disabled:cursor-not-allowed disabled:opacity-50"
            >
              <RotateCcw className="h-4 w-4" aria-hidden />
              Reset to default
            </button>
            {dirty && (
              <button
                type="button"
                onClick={discard}
                disabled={saving}
                className="inline-flex items-center gap-1.5 rounded-lg border border-gray-300 px-3 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-50"
              >
                <Undo2 className="h-4 w-4" aria-hidden />
                Discard
              </button>
            )}
            <button
              type="button"
              onClick={openSave}
              disabled={!dirty || hasErrors || saving}
              className="inline-flex items-center gap-1.5 rounded-lg bg-primary-600 px-4 py-2 text-sm font-semibold text-white shadow-sm hover:bg-primary-700 disabled:cursor-not-allowed disabled:opacity-50"
            >
              <Save className="h-4 w-4" aria-hidden />
              Save
            </button>
          </div>
        </div>
      )}

      {/* ── Save dialog ─────────────────────────────────────────────────── */}
      <Modal
        isOpen={saveOpen}
        onClose={saving ? () => undefined : () => setSaveOpen(false)}
        title="Save the Connect content policy"
        size="md"
        footer={
          <div className="flex w-full flex-col gap-3 sm:flex-row sm:justify-end">
            <button
              type="button"
              className="rounded-lg border border-gray-300 px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50"
              onClick={() => setSaveOpen(false)}
              disabled={saving}
            >
              Keep editing
            </button>
            <button
              type="button"
              className="inline-flex items-center justify-center gap-2 rounded-lg bg-primary-600 px-4 py-2 text-sm font-semibold text-white shadow-sm transition-colors hover:bg-primary-700 disabled:opacity-50"
              onClick={() => void confirmSave()}
              disabled={saving}
            >
              {saving && <Loader2 className="h-4 w-4 animate-spin" aria-hidden />}
              Save policy
            </button>
          </div>
        }
      >
        {saveOpen && baseline && (
          <div className="space-y-4 text-sm text-gray-700">
            {saveError && <ModalFormErrorBanner message={saveError} />}
            <p>
              The new version applies to posts, edits and comments made from now on. Nothing already posted is re-checked,
              and past AI reviews keep the version they were judged against.
            </p>
            {draft.mode === 'enforce' && (
              <p className="flex items-start gap-1.5 rounded-lg border border-amber-200 bg-amber-50 p-3 text-amber-900">
                <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" aria-hidden />
                <span>
                  Enforce is on: employees can’t share anything that breaks a Block rule, and Hold posts wait for a
                  moderator in Social moderation › AI review.
                </span>
              </p>
            )}
            <div>
              <p className="text-xs font-semibold uppercase tracking-wide text-gray-500">What changes</p>
              {(() => {
                const changes = summariseChanges(baseline, draft);
                return changes.length === 0 ? (
                  <p className="mt-1 text-gray-500">No differences from the active policy.</p>
                ) : (
                  <ul className="mt-1 list-disc space-y-0.5 pl-5">
                    {changes.map((c) => <li key={c}>{c}</li>)}
                  </ul>
                );
              })()}
            </div>
            <div>
              <label htmlFor="ccp-save-note" className="block text-xs font-semibold uppercase tracking-wide text-gray-500">
                Note (optional)
              </label>
              <textarea
                id="ccp-save-note"
                value={saveNote}
                maxLength={NOTE_MAX}
                onChange={(e) => setSaveNote(e.target.value)}
                rows={2}
                placeholder="Why this change — shown in the version history"
                className="mt-1 block w-full resize-y rounded-lg border border-gray-300 px-3 py-2 text-sm focus:border-primary-500 focus:outline-none focus:ring-1 focus:ring-primary-500"
              />
            </div>
          </div>
        )}
      </Modal>

      {/* ── Read-only view of one version ───────────────────────────────── */}
      <Modal
        isOpen={!!viewing}
        onClose={() => setViewing(null)}
        title={viewing ? (viewing.isDefault || viewing.version === 0 ? 'Built-in default' : `Version ${viewing.version}`) : ''}
        size="lg"
        footer={
          <div className="flex w-full flex-col gap-3 sm:flex-row sm:justify-end">
            <button
              type="button"
              className="rounded-lg border border-gray-300 px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50"
              onClick={() => setViewing(null)}
            >
              Close
            </button>
            {canEdit && viewing && (
              <button
                type="button"
                className="inline-flex items-center justify-center gap-2 rounded-lg bg-primary-600 px-4 py-2 text-sm font-semibold text-white shadow-sm hover:bg-primary-700"
                onClick={() => {
                  if (dirty && !window.confirm('Replace your unsaved changes in the editor with this version?')) return;
                  loadIntoEditor(viewing);
                }}
              >
                <History className="h-4 w-4" aria-hidden />
                Load into editor
              </button>
            )}
          </div>
        }
      >
        {viewing && <PolicyReadOnly stored={viewing} />}
      </Modal>
    </div>
  );
}

// ── Rule editor row ──────────────────────────────────────────────────────────

function RuleEditor({
  rule,
  editable,
  errors,
  onChange,
  onRemove,
}: {
  rule: PolicyRule;
  editable: boolean;
  errors: string[];
  onChange: (patch: Partial<PolicyRule>) => void;
  onRemove: () => void;
}) {
  const nameId = `ccp-rule-name-${rule.id}`;
  const actionId = `ccp-rule-action-${rule.id}`;
  const descId = `ccp-rule-desc-${rule.id}`;
  const invalid = errors.length > 0;
  return (
    <li className={`rounded-lg border bg-white p-4 ${invalid ? 'border-red-300' : 'border-gray-200'}`}>
      <div className="flex flex-col gap-3 sm:flex-row sm:items-start">
        <div className="min-w-0 flex-1">
          <label htmlFor={nameId} className="sr-only">Rule name</label>
          <input
            id={nameId}
            type="text"
            value={rule.name}
            maxLength={NAME_MAX}
            readOnly={!editable}
            placeholder="Rule name, e.g. Store keys and alarm codes"
            onChange={(e) => onChange({ name: e.target.value })}
            className="block w-full rounded-lg border border-gray-300 px-3 py-2 text-sm font-semibold text-gray-900 focus:border-primary-500 focus:outline-none focus:ring-1 focus:ring-primary-500 read-only:border-transparent read-only:bg-transparent read-only:px-0"
          />
        </div>
        <div className="flex items-center gap-2">
          <label htmlFor={actionId} className="sr-only">What happens when a post breaks this rule</label>
          <select
            id={actionId}
            value={rule.action}
            disabled={!editable}
            onChange={(e) => onChange({ action: e.target.value as RuleAction })}
            className={`w-full rounded-lg border px-3 py-2 text-sm font-medium focus:outline-none focus:ring-2 focus:ring-primary-500 disabled:cursor-default disabled:opacity-100 sm:w-44 ${ACTION_TONE[rule.action]}`}
          >
            {ACTION_OPTIONS.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
          </select>
          {editable && !rule.builtIn && (
            <button
              type="button"
              onClick={onRemove}
              aria-label={`Remove the rule ${rule.name.trim() || 'Untitled rule'}`}
              title="Remove this rule"
              className="shrink-0 rounded-lg border border-gray-300 p-2 text-gray-500 hover:bg-red-50 hover:text-red-600"
            >
              <Trash2 className="h-4 w-4" aria-hidden />
            </button>
          )}
        </div>
      </div>
      <label htmlFor={descId} className="sr-only">What this rule catches</label>
      <textarea
        id={descId}
        value={rule.description}
        readOnly={!editable}
        rows={descriptionRows(rule.description)}
        placeholder="What this rule catches, in plain words — the AI judges posts by this description."
        onChange={(e) => onChange({ description: e.target.value })}
        className="mt-3 block w-full resize-y rounded-lg border border-gray-300 px-3 py-2 text-sm leading-relaxed text-gray-700 focus:border-primary-500 focus:outline-none focus:ring-1 focus:ring-primary-500 read-only:bg-gray-50"
      />
      <div className="mt-1 flex items-center justify-between gap-2 text-xs text-gray-400">
        <span>{rule.builtIn ? 'Built-in rule — can be switched off but not removed' : 'Custom rule'}</span>
        <span className={rule.description.length > DESCRIPTION_MAX ? 'text-red-600' : undefined}>
          {rule.description.length}/{DESCRIPTION_MAX}
        </span>
      </div>
      {invalid && (
        <ul className="mt-2 space-y-0.5 text-sm text-red-700">
          {errors.map((e) => <li key={e}>{e}</li>)}
        </ul>
      )}
    </li>
  );
}

// ── Read-only policy (version viewer) ────────────────────────────────────────

function PolicyReadOnly({ stored }: { stored: StoredPolicy }) {
  const p = stored.policy;
  return (
    <div className="space-y-4 text-sm text-gray-700">
      <p className="text-gray-500">
        {stored.isDefault || stored.version === 0
          ? 'The built-in default every project starts with.'
          : <>Saved by {savedByText(stored)} on {fmtWhen(stored.createdAt)}{stored.note ? <> — “{stored.note}”</> : null}</>}
      </p>
      <p>
        <span className="font-semibold text-gray-900">Mode:</span> {MODE_LABEL[p.mode] ?? p.mode}
      </p>
      <div>
        <p className="font-semibold text-gray-900">Rules</p>
        <ul className="mt-2 divide-y divide-gray-100 rounded-lg border border-gray-200">
          {(p.rules ?? []).map((r) => (
            <li key={r.id} className="px-3 py-2">
              <div className="flex flex-wrap items-center justify-between gap-2">
                <span className="font-medium text-gray-900">{r.name}</span>
                <ActionChip action={r.action} />
              </div>
              <p className="mt-0.5 whitespace-pre-line text-gray-600">{r.description}</p>
            </li>
          ))}
        </ul>
      </div>
      <div>
        <p className="font-semibold text-gray-900">Always allowed</p>
        <p className="mt-1 whitespace-pre-line text-gray-600">{p.allowed || '—'}</p>
      </div>
      <div>
        <p className="font-semibold text-gray-900">Extra guidelines</p>
        <p className="mt-1 whitespace-pre-line text-gray-600">{p.guidelines || '—'}</p>
      </div>
    </div>
  );
}

// ── Version history ──────────────────────────────────────────────────────────

function VersionHistoryTable({
  versions,
  loading,
  error,
  activeVersion,
  canEdit,
  fetchingVersion,
  onRetry,
  onView,
  onLoad,
}: {
  versions: PolicyVersionSummary[] | null;
  loading: boolean;
  error: string | null;
  activeVersion: number;
  canEdit: boolean;
  fetchingVersion: number | null;
  onRetry: () => void;
  onView: (version: number) => void;
  onLoad: (version: number) => void;
}) {
  return (
    <section aria-labelledby="ccp-versions-title" className="overflow-hidden rounded-lg border border-gray-200 bg-white shadow-sm">
      <div className="border-b border-gray-100 px-4 py-3">
        <h2 id="ccp-versions-title" className="text-base font-semibold text-gray-900">Version history</h2>
        <p className="text-xs text-gray-500">Every save is a new version and applies from the moment it’s saved.</p>
      </div>
      {error && (
        <div className="flex flex-wrap items-center justify-between gap-2 px-4 py-3">
          <PageErrorBanner message={error} />
          <button
            type="button"
            onClick={onRetry}
            className="rounded-lg border border-gray-300 px-3 py-1.5 text-sm font-medium text-gray-700 hover:bg-gray-50"
          >
            Try again
          </button>
        </div>
      )}
      {loading && !versions ? (
        <InlineLoader label="Loading versions…" className="flex px-4 py-6" />
      ) : versions && versions.length === 0 ? (
        <p className="px-4 py-6 text-sm text-gray-500">
          No versions saved yet — this project uses the built-in default (Monitor only).
        </p>
      ) : versions ? (
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200 text-sm">
            <thead className="bg-gray-50/70 text-left text-xs font-semibold text-gray-500">
              <tr>
                <th scope="col" className="whitespace-nowrap px-4 py-3">Version</th>
                <th scope="col" className="whitespace-nowrap px-4 py-3">Mode</th>
                <th scope="col" className="whitespace-nowrap px-4 py-3">Saved</th>
                <th scope="col" className="px-4 py-3">Note</th>
                <th scope="col" className="sticky right-0 z-10 whitespace-nowrap bg-gray-100 px-6 py-3 text-center shadow-[-4px_0_6px_-4px_rgba(0,0,0,0.1)]">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100 bg-white">
              {versions.map((v) => {
                const active = v.version === activeVersion;
                return (
                  <tr key={v.version} className={`group ${active ? 'bg-primary-50' : 'hover:bg-gray-50'}`}>
                    <td className="whitespace-nowrap px-4 py-3 font-medium text-gray-900">
                      v{v.version}
                      {active && <span className="ml-1.5 text-xs font-normal text-primary-700">(active)</span>}
                    </td>
                    <td className="whitespace-nowrap px-4 py-3 text-gray-700">{MODE_LABEL[v.mode] ?? v.mode}</td>
                    <td className="whitespace-nowrap px-4 py-3 text-gray-700">
                      {fmtWhen(v.createdAt)}
                      <span className="block text-xs text-gray-500">by {savedByText(v)}</span>
                    </td>
                    <td className="min-w-[12rem] px-4 py-3 text-gray-600">{v.note || <span className="text-gray-400">—</span>}</td>
                    {/* The sticky cell is opaque, so it carries the active tint itself. */}
                    <td className={`sticky right-0 z-10 whitespace-nowrap px-6 py-3 text-center shadow-[-4px_0_6px_-4px_rgba(0,0,0,0.1)] ${active ? 'bg-primary-50' : 'bg-white group-hover:bg-gray-50'}`}>
                      <TableActionGroup>
                        <TableActionButton
                          action="view"
                          type="button"
                          label={`View v${v.version}`}
                          loading={fetchingVersion === v.version}
                          disabled={fetchingVersion !== null}
                          onClick={() => onView(v.version)}
                        />
                        {canEdit && (
                          <TableActionButton
                            action="restore"
                            type="button"
                            label={`Load v${v.version} into the editor`}
                            disabled={fetchingVersion !== null}
                            onClick={() => onLoad(v.version)}
                          />
                        )}
                      </TableActionGroup>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      ) : null}
    </section>
  );
}

// ── Test a post ──────────────────────────────────────────────────────────────

interface TestPhoto {
  key: string;
  name: string;
  blobPath: string;
  /** Local object URL for the thumbnail — revoked on removal and unmount. */
  previewUrl: string;
}

interface TestRun {
  verdict: AiVerdict;
  /** What was sent, in order — `mediaIndex` indexes into this. */
  sentPaths: string[];
  /** Policy + sample fingerprint at run time, to flag a stale result. */
  inputKey: string;
}

const DECISION_META: Record<AiVerdict['decision'], { label: string; tone: string }> = {
  allow: { label: 'Would publish', tone: 'border-emerald-200 bg-emerald-50 text-emerald-800' },
  hold: { label: 'Would hold', tone: 'border-amber-300 bg-amber-50 text-amber-900' },
  block: { label: 'Would block', tone: 'border-red-300 bg-red-50 text-red-800' },
  error: { label: 'Couldn’t check', tone: 'border-gray-300 bg-gray-100 text-gray-700' },
};

/**
 * Runs the AI on a sample post against the policy CURRENTLY IN THE EDITOR (sent as
 * `policy`, so unsaved changes can be tried before they reach anyone). Records
 * nothing server-side. Photos upload through the composer's route
 * (socialFeedAPI.uploadPhoto) because the test endpoint only accepts social-post
 * blob paths — the same ones a real post would carry.
 *
 * The chip reflects the AI's DECISION, i.e. what Enforce would do. Under Monitor
 * only or Off the same post would be published; the panel says so rather than
 * showing "Would publish" for a post that breaks a Block rule.
 */
function TestPostPanel({
  projectId,
  policy,
  policyKey,
  testsDraft,
  policyInvalid,
  activeLabel,
  aiAvailable,
}: {
  projectId: number;
  policy: ContentPolicy;
  policyKey: string;
  testsDraft: boolean;
  /** The editor has validation errors; the server would 400 the draft, so say why up front. */
  policyInvalid: boolean;
  activeLabel: string;
  aiAvailable: boolean;
}) {
  const [text, setText] = useState('');
  const [photos, setPhotos] = useState<TestPhoto[]>([]);
  const [uploading, setUploading] = useState(false);
  const [running, setRunning] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [run, setRun] = useState<TestRun | null>(null);
  const fileRef = useRef<HTMLInputElement>(null);

  // Revoke every preview URL on unmount (a project switch remounts this panel).
  const photosRef = useRef<TestPhoto[]>([]);
  photosRef.current = photos;
  useEffect(() => () => { photosRef.current.forEach((p) => URL.revokeObjectURL(p.previewUrl)); }, []);

  const inputKey = `${policyKey}\u0000${text}\u0000${photos.map((p) => p.blobPath).join('|')}`;
  const stale = !!run && run.inputKey !== inputKey;

  const addPhotos = async (files: FileList | null) => {
    if (!files?.length) return;
    const room = TEST_MAX_PHOTOS - photos.length;
    if (room <= 0) return;
    setUploading(true);
    setError(null);
    try {
      for (const file of Array.from(files).slice(0, room)) {
        if (file.size > TEST_PHOTO_MAX_BYTES) {
          setError(`“${file.name}” is larger than 7 MB — the AI check skips photos that big. Try a smaller one.`);
          continue;
        }
        const res = await socialFeedAPI.uploadPhoto(file);
        // /upload/file answers { success, data: { fileName, blobPath, url } }.
        const blobPath: string | undefined = res.data?.data?.blobPath;
        if (!blobPath) throw new Error('Upload returned no path');
        setPhotos((prev) => [...prev, {
          key: `${Date.now()}-${file.name}`,
          name: file.name,
          blobPath,
          previewUrl: URL.createObjectURL(file),
        }]);
      }
    } catch {
      setError('That photo could not be uploaded. Please try a different file.');
    } finally {
      setUploading(false);
      if (fileRef.current) fileRef.current.value = '';
    }
  };

  const removePhoto = (key: string) => {
    setPhotos((prev) => {
      const hit = prev.find((p) => p.key === key);
      if (hit) URL.revokeObjectURL(hit.previewUrl);
      return prev.filter((p) => p.key !== key);
    });
  };

  const canRun = aiAvailable && !policyInvalid && !running && !uploading && text.length <= TEST_TEXT_MAX
    && (text.trim().length > 0 || photos.length > 0);

  const runTest = async () => {
    if (!canRun) return;
    const sentPaths = photos.map((p) => p.blobPath);
    const key = inputKey;
    setRunning(true);
    setError(null);
    try {
      const verdict = await connectPolicyAPI.testPolicy(projectId, {
        text: text.trim() || undefined,
        mediaBlobPaths: sentPaths,
        policy,
      });
      setRun({ verdict, sentPaths, inputKey: key });
    } catch (err) {
      setRun(null);
      setError(connectPolicyErrorMessage(err, 'Could not run the test. Please try again.'));
    } finally {
      setRunning(false);
    }
  };

  const flaggedPaths = useMemo(() => {
    const s = new Set<string>();
    if (!run || stale) return s;
    for (const v of run.verdict.violations ?? []) {
      if (v.mediaIndex !== null && v.mediaIndex >= 0 && v.mediaIndex < run.sentPaths.length) s.add(run.sentPaths[v.mediaIndex]);
    }
    return s;
  }, [run, stale]);

  const verdict = run?.verdict ?? null;
  const meta = verdict ? DECISION_META[verdict.decision] ?? DECISION_META.error : null;
  const flagged = verdict && (verdict.decision === 'hold' || verdict.decision === 'block');

  return (
    <section aria-labelledby="ccp-test-title" className="rounded-lg border border-gray-100 bg-white p-4 sm:p-6">
      <div className="flex items-center gap-2">
        <ShieldCheck className="h-5 w-5 text-primary-600" aria-hidden />
        <h2 id="ccp-test-title" className="text-lg font-semibold text-gray-900">Test a post</h2>
      </div>
      <p className="mt-1 text-sm text-gray-500">
        {testsDraft
          ? 'Checks a sample post against your unsaved changes. Nothing is posted or recorded.'
          : `Checks a sample post against ${activeLabel}. Nothing is posted or recorded.`}
      </p>

      {!aiAvailable && (
        <p className="mt-3 rounded-lg border border-amber-200 bg-amber-50 px-3 py-2 text-sm text-amber-900">
          Testing needs AI review, which is switched off on this server.
        </p>
      )}

      {aiAvailable && policyInvalid && (
        <p className="mt-3 rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-800">
          Fix the highlighted rules first — the test uses the rules in the editor.
        </p>
      )}

      <label htmlFor="ccp-test-text" className="sr-only">Sample post text</label>
      <textarea
        id="ccp-test-text"
        value={text}
        onChange={(e) => setText(e.target.value)}
        rows={5}
        placeholder="Type or paste a post, e.g. “Great sales day at the Andheri store! 🎉”"
        className="mt-3 block w-full resize-y rounded-lg border border-gray-300 px-3 py-2 text-sm focus:border-primary-500 focus:outline-none focus:ring-1 focus:ring-primary-500"
      />
      <p className={`mt-1 text-right text-xs ${text.length > TEST_TEXT_MAX ? 'text-red-600' : 'text-gray-400'}`}>
        {text.length}/{TEST_TEXT_MAX}
      </p>

      {photos.length > 0 && (
        <ul className="mt-2 grid grid-cols-4 gap-2">
          {photos.map((p, i) => {
            const hit = flaggedPaths.has(p.blobPath);
            return (
              <li key={p.key} className="relative">
                <img
                  src={p.previewUrl}
                  alt={`Photo ${i + 1}: ${p.name}`}
                  className={`aspect-square w-full rounded-lg object-cover ${hit ? 'ring-2 ring-red-500 ring-offset-1' : 'border border-gray-200'}`}
                />
                <span className="absolute bottom-1 left-1 rounded bg-black/60 px-1 text-[10px] font-medium text-white">
                  {i + 1}
                </span>
                <button
                  type="button"
                  onClick={() => removePhoto(p.key)}
                  aria-label={`Remove photo ${i + 1}`}
                  className="absolute -right-1.5 -top-1.5 rounded-full border border-gray-200 bg-white p-0.5 text-gray-600 shadow-sm hover:text-red-600"
                >
                  <X className="h-3.5 w-3.5" aria-hidden />
                </button>
              </li>
            );
          })}
        </ul>
      )}

      <div className="mt-3 flex flex-wrap items-center gap-2">
        <input
          ref={fileRef}
          type="file"
          accept="image/jpeg,image/png,image/webp"
          multiple
          className="hidden"
          onChange={(e) => void addPhotos(e.target.files)}
        />
        <button
          type="button"
          onClick={() => fileRef.current?.click()}
          disabled={uploading || running || photos.length >= TEST_MAX_PHOTOS}
          className="inline-flex items-center gap-1.5 rounded-lg border border-gray-300 px-3 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50 disabled:cursor-not-allowed disabled:opacity-50"
        >
          {uploading ? <Loader2 className="h-4 w-4 animate-spin" aria-hidden /> : <ImageIcon className="h-4 w-4" aria-hidden />}
          {uploading ? 'Uploading…' : `Add photo${photos.length ? ` (${photos.length}/${TEST_MAX_PHOTOS})` : 's'}`}
        </button>
        <button
          type="button"
          onClick={() => void runTest()}
          disabled={!canRun}
          className="ml-auto inline-flex items-center gap-1.5 rounded-lg bg-primary-600 px-4 py-2 text-sm font-semibold text-white shadow-sm hover:bg-primary-700 disabled:cursor-not-allowed disabled:opacity-50"
        >
          {running ? <Loader2 className="h-4 w-4 animate-spin" aria-hidden /> : <Sparkles className="h-4 w-4" aria-hidden />}
          {running ? 'Checking…' : 'Run test'}
        </button>
      </div>

      {error && <div className="mt-3"><PageErrorBanner message={error} /></div>}

      {verdict && meta && (
        <div className={`mt-4 space-y-3 border-t border-gray-100 pt-4 ${stale ? 'opacity-60' : ''}`} aria-live="polite">
          <div className="flex flex-wrap items-center gap-2">
            <span className={`inline-flex items-center rounded-full border px-2.5 py-0.5 text-sm font-semibold ${meta.tone}`}>
              {meta.label}
            </span>
            {verdict.summary && <span className="text-sm text-gray-700">{verdict.summary}</span>}
          </div>

          {stale && (
            <p className="text-xs font-medium text-gray-600">
              The rules or the sample changed since this test — run it again to see the new result.
            </p>
          )}

          {verdict.decision === 'error' && (
            <p className="text-sm text-gray-600">
              {verdict.error ? `${verdict.error} ` : ''}
              In Enforce, a post the AI can’t check is shared and marked Not checked for a moderator.
            </p>
          )}

          {flagged && policy.mode !== 'enforce' && (
            <p className="text-sm text-gray-600">
              {policy.mode === 'monitor'
                ? 'That is what Enforce would do. In Monitor only this post would be shared, and flagged in Social moderation › AI review.'
                : 'That is what Enforce would do. With the policy Off, this post would be shared without a check.'}
            </p>
          )}

          {(verdict.violations ?? []).length > 0 && (
            <ul className="space-y-2">
              {verdict.violations.map((v, i) => <ViolationItem key={`${v.ruleId}-${i}`} v={v} />)}
            </ul>
          )}

          <p className="text-xs text-gray-400">
            {[
              verdict.model,
              Number.isFinite(verdict.latencyMs) ? `${(verdict.latencyMs / 1000).toFixed(1)} s` : null,
            ].filter(Boolean).join(' · ')}
          </p>
        </div>
      )}
    </section>
  );
}

function ViolationItem({ v }: { v: Violation }) {
  return (
    <li className="rounded-lg border border-gray-200 p-3 text-sm">
      <div className="flex flex-wrap items-center gap-1.5">
        <span className="font-semibold text-gray-900">{v.ruleName}</span>
        <ActionChip action={v.action} />
        <span className="text-xs text-gray-500">{v.confidence} confidence</span>
        {v.mediaIndex !== null && (
          <span className="rounded bg-gray-100 px-1.5 py-0.5 text-xs font-medium text-gray-700">Photo {v.mediaIndex + 1}</span>
        )}
      </div>
      {v.reason && <p className="mt-1 text-gray-700">{v.reason}</p>}
      {v.fix && (
        <p className="mt-1 text-gray-600">
          <span className="font-medium text-gray-700">Told to the author:</span> {v.fix}
        </p>
      )}
    </li>
  );
}
